import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../context/AuthContext';
import API from '../api';
import '../styles/global.css';
import logoImage from '../assets/logoo.png';
import ChatBot from '../components/ChatBot';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    loading: authLoading,
    logout: authLogout,
    getDaysRemaining,
    hasPermission
  } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showLogoutMessage, setShowLogoutMessage] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expiryCount, setExpiryCount] = useState(0);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMenuOpen(false);
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Superadmin tenant selection states
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState(
    localStorage.getItem('selectedTenantId') || ''
  );

  useEffect(() => {
    if (user?.role === 'superadmin') {
      const fetchPharmacies = async () => {
        try {
          const { data } = await API.get('/users/admin/users');
          setPharmacies(data.filter(u => u.role === 'admin') || []);
        } catch (err) {
          console.error("Failed to load pharmacies", err);
        }
      };
      fetchPharmacies();
    }
  }, [user]);

  const handlePharmacyChange = (e) => {
    const id = e.target.value;
    setSelectedPharmacyId(id);
    if (id) {
      localStorage.setItem('selectedTenantId', id);
    } else {
      localStorage.removeItem('selectedTenantId');
    }
    navigate('/dashboard');
    window.location.reload();
  };

  const allLinks = useMemo(() => [
    { path: '/dashboard', label: 'Home', permission: 'dashboard', alwaysShow: true },
    { path: '/parties', label: 'Parties', permission: 'parties' },
    { path: '/transactions', label: 'Transactions', permission: 'transactions' },
    { path: '/medicines', label: 'Medicines', permission: 'medicines' },
    { path: '/purchases', label: 'Purchases', permission: 'purchases' },
    { path: '/returns', label: 'Returns', permission: 'purchases' },
    {path: '/damages', label: 'Damages', permission: 'damages' },
    { path: '/billing', label: 'Billing', permission: 'billing' },
    { path: '/sales', label: 'Sales', permission: 'sales' },
    { path: '/reports', label: 'Reports', permission: 'reports' },
    { path: '/expiry', label: 'Expiry', permission: 'expiry' },
    { path: '/profile', label: 'Profile', permission: 'profile' },
  ], []);

  const links = useMemo(() => {
    if (!user) return [];
    if (user.role === 'superadmin') {
      const selectedTenant = localStorage.getItem('selectedTenantId');
      if (!selectedTenant) {
        return [
          { path: '/admin/dashboard', label: 'Admin Panel', alwaysShow: true }
        ];
      }
    }

    const visible = allLinks.filter(link => {
      if (link.alwaysShow) return true;
      if (link.adminOnly && user.role === 'staff') return false;
      return hasPermission(link.permission);
    }).map(link => ({
      ...link,
      label: link.path === '/dashboard'
        ? (user.role === 'staff' ? 'Home' : 'Dashboard')
        : link.label
    }));

    if (user.role === 'admin' || user.role === 'superadmin') {
      visible.push({ path: '/staff', label: 'Staff Payroll', alwaysShow: true });
    }

    return visible;
  }, [allLinks, user, hasPermission]);

  const fetchExpiryCount = useCallback(async () => {
    if (!user) return;
    if (!hasPermission('expiry') && user.role !== 'superadmin') return;
    try {
      const { data } = await API.get('/medicines/near-expiry');
      if (Array.isArray(data)) {
        setExpiryCount(data.length);
      }
    } catch (error) {
      console.error('Failed to fetch expiry count', error);
    }
  }, [user, hasPermission]);

  useEffect(() => {
    fetchExpiryCount();
  }, [fetchExpiryCount, location.pathname]);

  const shouldShowBanner = useMemo(() => {
    if (!user || bannerDismissed || isLoggingOut) return false;
    if (user.role === 'superadmin') return false;
    if (user.role === 'staff') return false;
    if (user.status === 'active') return false;
    if (user.status === 'trial' && user.trial && !user.trial.isExpired) return true;
    if (['expired', 'blocked', 'inactive'].includes(user.status)) return true;
    if (user.status === 'trial' && user.trial?.isExpired) return true;
    return false;
  }, [user, bannerDismissed, isLoggingOut]);

  const getBannerContent = useCallback(() => {
    if (!user) return null;
    const daysRemaining = getDaysRemaining();

    if (user.status === 'trial') {
      if (daysRemaining <= 0) {
        return {
          message: 'Your free trial has expired. Please subscribe to continue accessing PharmaLedger.',
          buttonText: 'Subscribe Now',
          canDismiss: false,
          style: { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff' }
        };
      }
      return {
        message: `You are on a free trial. ${daysRemaining} day${daysRemaining > 1 ? 's' : ''} remaining.`,
        buttonText: 'Upgrade Plan',
        canDismiss: true,
        style: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff' }
      };
    }

    if (['expired', 'blocked', 'inactive'].includes(user.status)) {
      return {
        message: 'Your subscription is inactive. Please renew to continue.',
        buttonText: 'Renew Now',
        canDismiss: false,
        style: { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff' }
      };
    }

    return null;
  }, [user, getDaysRemaining]);

  const logout = async () => {
    setIsLoggingOut(true);
    setProfileOpen(false);
    setShowLogoutMessage(true);

    await new Promise(resolve => setTimeout(resolve, 1000));

    localStorage.removeItem('token');
    localStorage.removeItem('selectedTenantId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');

    authLogout();
    setShowLogoutMessage(false);

    window.location.hash = '#/login';
    window.location.reload();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  if (authLoading && !isLoggingOut) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: '20px' }}>
        <div style={{ width: '50px', height: '50px', border: '4px solid #e0f2fe', borderTop: '4px solid #1880c2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b' }}>Loading...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const bannerContent = getBannerContent();
  const sidebarWidth = sidebarOpen ? '260px' : '80px';
  const firstName = user?.name ? user.name.split(' ')[0] : 'there';

  const styles = {
    layoutContainer: { display: 'flex', minHeight: '100vh', background: '#f4f9fd', pointerEvents: isLoggingOut ? 'none' : 'auto' },

    sidebar: {
      width: isMobile ? (menuOpen ? '260px' : '0px') : sidebarWidth,
      background: '#1880c2',
      transition: 'width 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      position: isMobile ? 'fixed' : 'sticky',
      top: 0, bottom: 0, left: 0,
      height: '100vh',
      zIndex: 9999,
      overflow: 'hidden',
      boxShadow: '4px 0 16px rgba(24, 128, 194, 0.25)'
    },
    sidebarLogo: {
      padding: '20px 24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#ffffff',
      cursor: 'pointer'
    },
    logoText: { fontWeight: 800, fontSize: 16, display: sidebarOpen || isMobile ? 'block' : 'none' },
    menuNav: { 
      flex: 1, 
      padding: '24px 0', 
      overflowY: 'auto',
      scrollbarWidth: 'none', 
      msOverflowStyle: 'none', 
      '&::-webkit-scrollbar': { display: 'none' }
    },
    navLink: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '14px 24px',
      color: active ? '#ffffff' : 'rgba(255, 255, 255, 0.75)',
      textDecoration: 'none',
      background: active ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
      borderLeft: active ? '4px solid #ffffff' : '4px solid transparent',
      fontWeight: active ? 600 : 500,
      fontSize: 14,
      transition: 'all 0.2s'
    }),
    sidebarFooter: {
      padding: '16px 20px',
      borderTop: '1px solid rgba(255, 255, 255, 0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#ffffff',
      cursor: 'pointer'
    },
    userAvatar: {
      width: 36, height: 36, borderRadius: '50%',
      background: '#ffffff',
      color: '#1880c2',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0
    },
    userText: { display: sidebarOpen || isMobile ? 'block' : 'none', flex: 1, overflow: 'hidden' },

    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      marginLeft: 0,
      position: 'relative'
    },
    topBar: {
      background: '#ffffff',
      borderBottom: '1px solid #e0f2fe',
      padding: '16px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0, 
      zIndex: 1000
    },
    hamburgerBtn: {
      background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: '#1880c2',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
    },
    bodyPanel: { flex: 1, padding: 0, overflowX: 'hidden' }
  };

  return (
    <>
      {/* 
        ✅ RESPONSIVE CSS INJECTED HERE
        Ensures the notifications dropdown fits mobile viewports
        and makes the top bar flex wrapping clean.
      */}
      <style>{`
        .sidebar-nav { scrollbar-width: none; -ms-overflow-style: none; }
        .sidebar-nav::-webkit-scrollbar { display: none; }

        /* General Top Bar Responsiveness */
        .topbar-left, .topbar-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        /* Responsive Greeting */
        .greeting-header { font-size: 18px; }

        /* Notification Dropdown Safety Catch */
        .notification-wrapper { position: relative; display: flex; align-items: center; }

        @media (max-width: 768px) {
          .top-bar-container { padding: 12px 16px !important; }
          .topbar-left { gap: 10px; }
          .topbar-right { gap: 10px; }
          
          .greeting-header { font-size: 15px; }
          .greeting-icon { display: none; } /* Hide waving hand on small mobile to save space */
          
          /* Forces notification dropdowns to be fixed to viewport on mobile, preventing side scroll */
          .notification-wrapper > div[class*="dropdown"],
          .notification-wrapper > div[class*="menu"],
          .notification-wrapper > div:not(:first-child) {
            position: fixed !important;
            top: 65px !important;
            right: 10px !important;
            width: calc(100vw - 20px) !important;
            max-width: 380px !important;
            z-index: 10001 !important;
          }

          .superadmin-select { max-width: 100px; font-size: 11px !important; padding: 4px 8px !important; }
          
          /* Make the Banner stack beautifully */
          .trial-banner { flex-direction: column !important; text-align: center !important; gap: 10px !important; }
        }

        @media (max-width: 400px) {
           .greeting-header { font-size: 14px; }
           .greeting-firstName { display: none; } /* On extremely small screens, just show Good Morning */
        }
      `}</style>

      <div style={styles.layoutContainer}>

        {showLogoutMessage && (
          <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', background: 'white', borderLeft: '4px solid #1880c2', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', padding: '16px 24px', borderRadius: '12px', zIndex: 99999, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <strong style={{ color: '#1880c2' }}>Logging out securely...</strong>
          </div>
        )}

        {isMobile && menuOpen && (
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(24, 128, 194, 0.4)', backdropFilter: 'blur(2px)', zIndex: 9998 }}
          />
        )}

        <aside style={styles.sidebar}>
          <div style={styles.sidebarLogo} onClick={() => navigate('/dashboard')}>
            <div style={{
              width: 40, height: 40, borderRadius: '12px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(255, 255, 255, 0.3)', fontWeight: 900, fontSize: 18, color: '#ffffff', letterSpacing: '-0.5px', flexShrink: 0
            }}>
              Rx
            </div>
            <span style={styles.logoText}>PharmaLedger</span>
          </div>

          <nav style={styles.menuNav} className="sidebar-nav">
            {links.map(link => {
              const active = location.pathname === link.path;
              return (
                <Link key={link.path} to={link.path} onClick={() => isMobile && setMenuOpen(false)} style={styles.navLink(active)}>
                  {(sidebarOpen || isMobile) && (
                    <span style={{ flex: 1 }}>{link.label}</span>
                  )}
                  {link.label === 'Expiry' && expiryCount > 0 && (sidebarOpen || isMobile) && (
                    <span style={{ background: '#ef4444', color: 'white', fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>
                      {expiryCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {user && (
            <div style={styles.sidebarFooter} onClick={() => setProfileOpen(!profileOpen)}>
              <div style={styles.userAvatar}>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div style={styles.userText}>
                <div style={{ fontWeight: 600, fontSize: 13, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{user.name}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {user.role === 'superadmin' ? 'Super Admin' : (user.pharmacyName || 'Pharmacy Admin')}
                </div>
              </div>
              {(sidebarOpen || isMobile) && (
                <button
                  onClick={(e) => { e.stopPropagation(); logout(); }}
                  style={{ border: 'none', background: 'none', color: '#ffb3b3', cursor: 'pointer', fontSize: 16, padding: 4 }}
                  title="Logout"
                >
                  ⏻
                </button>
              )}
            </div>
          )}
        </aside>

        <div style={styles.mainContent}>
          {/* ✅ DYNAMIC BANNER - Positioned naturally in flow so it pushes content down gracefully on wrap */}
          {shouldShowBanner && bannerContent && (
            <div className="trial-banner" style={{ ...bannerContent.style, padding: '12px 20px', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', width: '100%', zIndex: 999 }}>
              <span>{bannerContent.message}</span>
              <button onClick={() => navigate('/subscription')} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>{bannerContent.buttonText}</button>
              {bannerContent.canDismiss && <button onClick={() => setBannerDismissed(true)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '20px', cursor: 'pointer', padding: '0 5px' }}>×</button>}
            </div>
          )}

          <header style={styles.topBar} className="top-bar-container">
            <div className="topbar-left">
              <button
                onClick={() => isMobile ? setMenuOpen(!menuOpen) : setSidebarOpen(!sidebarOpen)}
                style={styles.hamburgerBtn}
              >
                ≡
              </button>
              
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h1 className="greeting-header" style={{ margin: 0, fontWeight: 700, color: '#1a202c', whiteSpace: 'nowrap' }}>
                  {getGreeting()}, <span className="greeting-firstName" style={{ color: '#1880c2' }}>{firstName}!</span> <span className="greeting-icon">👋</span>
                </h1>
                {!isMobile && (
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginTop: 2 }}>
                    {user?.role === 'superadmin' 
                      ? 'Welcome to the global administration panel.' 
                      : 'Have a great day managing your pharmacy.'}
                  </span>
                )}
              </div>
            </div>

            <div className="topbar-right">
              {/* ✅ NOTIFICATION WRAPPER */}
              <div className="notification-wrapper">
                <NotificationBell />
              </div>

              {user && user.role === 'superadmin' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {!isMobile && (
                    <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '12px', background: 'linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%)', color: '#991b1b', fontWeight: '600', border: '1px solid #f87171' }}>
                      SuperAdmin Mode
                    </span>
                  )}
                  <select
                    className="superadmin-select"
                    value={selectedPharmacyId}
                    onChange={handlePharmacyChange}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #1880c2', fontSize: '12px', fontWeight: '600', background: 'white', cursor: 'pointer', outline: 'none', color: '#1880c2' }}
                  >
                    <option value="">-- Inspect Pharmacy --</option>
                    {pharmacies.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.pharmacyName || p.name}
                      </option>
                    ))}
                  </select>
                  {selectedPharmacyId && (
                    <button
                      onClick={() => {
                        localStorage.removeItem('selectedTenantId');
                        setSelectedPharmacyId('');
                        navigate('/admin/dashboard');
                        window.location.reload();
                      }}
                      style={{ padding: '4px 8px', background: '#e0f2fe', border: '1px solid #1880c2', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', color: '#1880c2' }}
                    >
                      Exit
                    </button>
                  )}
                </div>
              )}

              {user && user.role !== 'superadmin' && !isMobile && (
                <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '12px', background: user.role === 'staff' ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : '#e0f2fe', color: user.role === 'staff' ? '#b45309' : '#1880c2', fontWeight: '700', border: user.role === 'staff' ? '1px solid #fde68a' : '1px solid #bae6fd', whiteSpace: 'nowrap' }}>
                  {user.role === 'staff' ? 'Staff Member' : 'Pharmacy Owner'}
                </span>
              )}
            </div>
          </header>

          <div style={styles.bodyPanel}>
            <Outlet />
          </div>

          <ChatBot />
        </div>
      </div>
    </>
  );
}